import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useNotificationStore } from '../stores/notificationStore';
import socketService from '../services/socket';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Clock,
  FileText,
  Settings,
  LogOut,
  User,
  Menu,
  X,
  Sun,
  Moon,
  UserCircle,
  Settings as SettingsIcon,
  Landmark,
  CalendarDays,
  CalendarClock,
  BrainCircuit,
  Building2,
  ClipboardCheck,
  Shield,
  Bell,
  Activity,
  MapPin,
  Camera,
  BarChart3,
  UserCog,
  ChevronDown,
  Boxes,
  Utensils,
} from 'lucide-react';
import NotificationPanel from './NotificationPanel';
import MealTravelPromptHost from './MealReporting/MealTravelPromptHost';
import { useSiteConfigStore } from '../stores/siteConfigStore';
import api from '../services/api';

// Icon mapping from backend
const iconMap = {
  LayoutDashboard,
  UserCircle,
  Calendar,
  CalendarDays,
  Users,
  User,
  Clock,
  CalendarClock,
  FileText,
  Landmark,
  Settings,
  BrainCircuit,
  Building2,
  ClipboardCheck,
  Shield,
  Activity,
  MapPin,
  BarChart3,
  Boxes,
  Utensils,
};

// ── Nhóm điều hướng (gom sidebar phẳng thành các nhóm gập) ────────────────────
// Áp cho CẢ default nav lẫn nav từ pagePermissions (khớp theo href).
const NAV_GROUPS = [
  { id: 'overview', label: 'Tổng quan', hrefs: ['/', '/portal'] },
  {
    id: 'attendance',
    label: 'Chấm công',
    hrefs: ['/attendance', '/overtime-requests', '/off-site-attendance', '/attendance-management'],
  },
  { id: 'leave', label: 'Nghỉ phép & Duyệt', hrefs: ['/leave-requests', '/approval-process'] },
  {
    id: 'hr',
    label: 'Nhân sự',
    hrefs: ['/employee-management', '/face-registration', '/face-enrollment', '/department-delegation'],
  },
  {
    id: 'shift',
    label: 'Ca & Lịch',
    hrefs: ['/shifts', '/scheduling', '/discovery-policy', '/discovery-pilot', '/discovery-templates', '/discovery-analytics'],
  },
  {
    id: 'report',
    label: 'Báo cáo & Giám sát',
    hrefs: ['/reports', '/supervision', '/operations-monitoring', '/ai-auto-check', '/ai-analytics'],
  },
  { id: 'asset', label: 'Tài sản', hrefs: ['/asset-management'] },
  { id: 'meal', label: 'Căn tin', hrefs: ['/meal-reporting'] },
  { id: 'system', label: 'Hệ thống', hrefs: ['/hr-management', '/system-management', '/payroll-approval', '/payroll-view'] },
];

const groupIdOf = (href) => {
  const g = NAV_GROUPS.find((grp) => grp.hrefs.includes(href));
  return g ? g.id : 'other';
};

// Tiêu đề cho route không nằm trong nav
const EXTRA_TITLES = {
  '/profile': 'Hồ sơ cá nhân',
  '/notifications': 'Thông báo',
  '/payroll-approval': 'Duyệt bảng lương',
  '/payroll-view': 'Xem bảng lương',
  '/ai-analytics': 'Phân tích AI',
};

const COLLAPSE_KEY = 'nav_collapsed_groups';

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, pagePermissions, loadPagePermissions } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  const [isLoadingPages, setIsLoadingPages] = useState(true);
  const [canDelegate, setCanDelegate] = useState(false);
  // Số nhắc nhở cạnh mục sidebar: { '/approval-process': {count, level}, ... }
  const [navBadges, setNavBadges] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(COLLAPSE_KEY) || '[]'));
    } catch {
      return new Set();
    }
  });
  const { unreadCount, fetchUnreadCount, fetchSettings, handleNewNotification, togglePanel, isPanelOpen, initPush } =
    useNotificationStore();
  const { accessToken } = useAuthStore();
  const siteConfig = useSiteConfigStore((s) => s.config);

  // Đồng bộ state darkMode với class .dark THẬT trên <html> (chống lệch state):
  // no-flash script/toggle/OS-preference đều đổi class → observer cập nhật state →
  // các inline-style brand của siteConfig (gate theo !darkMode) luôn đúng ở dark mode.
  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setDarkMode(el.classList.contains('dark'));
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  // Real-time notifications via Socket.IO + fallback polling
  useEffect(() => {
    if (!user?.employee_id || !accessToken) return;
    fetchUnreadCount();
    fetchSettings();

    socketService.connect(accessToken);
    initPush().catch(() => {});

    const onNewNotif = (notif) => {
      handleNewNotification(notif);
    };
    socketService.on('notification:new', onNewNotif);

    const interval = setInterval(fetchUnreadCount, 60000);

    return () => {
      clearInterval(interval);
      socketService.off('notification:new', onNewNotif);
      socketService.disconnect();
    };
  }, [user?.employee_id, accessToken, fetchUnreadCount, fetchSettings, handleNewNotification, initPush]);

  // Default navigation (fallback khi chưa có page permissions từ API)
  // QUAN TRỌNG: Phải khớp với default_roles trong database
  const defaultNavigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER'] },
    { name: 'Thông tin cá nhân', href: '/portal', icon: UserCircle, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER', 'EMPLOYEE'] },
    { name: 'Chấm công', href: '/attendance', icon: Calendar, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER', 'EMPLOYEE'] },
    { name: 'Nghỉ phép', href: '/leave-requests', icon: CalendarDays, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER', 'EMPLOYEE'] },
    { name: 'Phê duyệt', href: '/approval-process', icon: ClipboardCheck, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER'] },
    { name: 'Tăng ca (OT)', href: '/overtime-requests', icon: Clock, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER', 'EMPLOYEE'] },
    { name: 'Chấm công ngoài', href: '/off-site-attendance', icon: MapPin, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER', 'EMPLOYEE'] },
    { name: 'Quản lý chấm công', href: '/attendance-management', icon: ClipboardCheck, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER'] },
    { name: 'Quản lý nhân viên', href: '/employee-management', icon: Users, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER'] },
    { name: 'Đăng ký khuôn mặt', href: '/face-registration', icon: User, roles: ['ADMIN', 'GENERAL_DIRECTOR'] },
    { name: 'Đăng ký mặt (Guided)', href: '/face-enrollment', icon: Camera, roles: ['ADMIN', 'GENERAL_DIRECTOR'] },
    { name: 'Ca làm việc', href: '/shifts', icon: Clock, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER'] },
    { name: 'Điều độ & Lập lịch', href: '/scheduling', icon: CalendarClock, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER'] },
    { name: 'Báo cáo', href: '/reports', icon: FileText, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER'] },
    { name: 'HRM', href: '/hr-management', icon: Landmark, roles: ['ADMIN', 'GENERAL_DIRECTOR'] },
    { name: 'Giám sát', href: '/supervision', icon: Shield, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR'] },
    { name: 'Giám sát vận hành', href: '/operations-monitoring', icon: Activity, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER'] },
    { name: 'Quản lý tài sản', href: '/asset-management', icon: Boxes, roles: ['ADMIN', 'GENERAL_DIRECTOR'] },
    { name: 'Báo cơm', href: '/meal-reporting', icon: Utensils, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'VICE_PRESIDENT', 'MANAGER', 'EMPLOYEE'] },
    { name: 'AI Chấm Công', href: '/ai-auto-check', icon: BrainCircuit, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR'] },
    { name: 'Quản lý hệ thống', href: '/system-management', icon: SettingsIcon, roles: ['ADMIN', 'GENERAL_DIRECTOR'] },
    { name: 'Discovery Policy', href: '/discovery-policy', icon: Shield, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR'] },
    { name: 'Discovery Pilot', href: '/discovery-pilot', icon: Activity, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR', 'MANAGER'] },
    { name: 'Ca mẫu Discovery', href: '/discovery-templates', icon: CalendarClock, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR'] },
    { name: 'Phân tích Discovery', href: '/discovery-analytics', icon: BarChart3, roles: ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR'] },
  ];

  // Load page permissions on mount
  useEffect(() => {
    const loadPages = async () => {
      setIsLoadingPages(true);
      try {
        await loadPagePermissions();
      } catch (error) {
        console.error('Failed to load page permissions:', error);
      } finally {
        setIsLoadingPages(false);
      }
    };

    if (user) {
      loadPages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.employee_id]);

  const deptSchedulingEnabled =
    ['ADMIN', 'GENERAL_DIRECTOR', 'DIRECTOR'].includes(user?.role?.role_code) || !!user?.department?.allow_scheduling;

  // Build navigation from page permissions or use default
  const buildNavigation = () => {
    if (isLoadingPages) {
      return [];
    }

    if (!pagePermissions || pagePermissions.length === 0) {
      return defaultNavigation
        .filter((item) => item.roles.includes(user?.role?.role_code))
        .filter((item) => {
          if (item.href === '/scheduling' && !deptSchedulingEnabled) return false;
          return true;
        });
    }

    const flattenPages = (pages, result = []) => {
      for (const page of pages) {
        if (page.is_menu_item !== false && page.permissions?.can_view !== false) {
          if (page.page_path === '/scheduling' && !deptSchedulingEnabled) continue;
          const Icon = iconMap[page.icon] || FileText;
          result.push({
            name: page.page_name,
            href: page.page_path,
            icon: Icon,
            permissions: page.permissions,
            children: page.children?.filter((c) => c.is_menu_item !== false && c.permissions?.can_view !== false) || [],
          });
        }
        if (page.children && page.children.length > 0) {
          for (const child of page.children) {
            if (child.is_menu_item !== false && child.permissions?.can_view !== false) {
              const ChildIcon = iconMap[child.icon] || FileText;
              result.push({
                name: child.page_name,
                href: child.page_path,
                icon: ChildIcon,
                permissions: child.permissions,
                isChild: true,
              });
            }
          }
        }
      }
      return result;
    };

    return flattenPages(pagePermissions);
  };

  /**
   * SỐ NHẮC NHỞ TRÊN SIDEBAR.
   *
   * Trước đây số việc đang chờ chỉ nhìn thấy khi đã MỞ trang Phê duyệt — tức là phải
   * nhớ mà vào xem. Đơn treo 25–84 ngày trên hệ thống này phần lớn không phải vì ai
   * từ chối làm, mà vì không ai biết là có việc.
   *
   * Dùng NGUYÊN nguồn của hộp duyệt (`/approval-inbox/summary`) chứ không đếm lại:
   *   • Phê duyệt  = mọi hàng đợi đang chờ CHÍNH tôi duyệt (7 loại đơn).
   *   • Nghỉ phép  = đơn nghỉ chờ tôi duyệt + đơn nghỉ CỦA TÔI đang treo — trang đó
   *                  chứa cả hai vai, và người gửi cũng cần một dấu hiệu là đơn của
   *                  mình vẫn chưa đi tới đâu.
   * `sample=0` để chỉ lấy con số, không kéo theo danh sách.
   *
   * Màu lấy theo ngưỡng nhắc/leo thang đã cấu hình: xám→có việc, hổ phách→quá hạn nhắc,
   * đỏ→quá hạn leo thang. Nhân viên thường thì cả hai đều bằng 0 nên không hiện gì.
   */
  useEffect(() => {
    if (!user?.employee_id) return;
    let cancelled = false;

    const load = async () => {
      try {
        const res = await api.get('/approval-inbox/summary', { params: { sample: 0 } });
        if (cancelled) return;
        const approver = res.data?.data?.approver || {};
        const mine = res.data?.data?.mine || {};
        const leaveQueue = (approver.queues || []).find((q) => q.key === 'leave');
        const myLeave = (mine.items || []).filter((i) => i.kind === 'Nghỉ phép');
        const worst = (...levels) => (levels.includes('overdue') ? 'overdue'
          : levels.includes('warn') ? 'warn' : 'ok');

        setNavBadges({
          '/approval-process': {
            count: approver.total || 0,
            level: approver.level || 'ok',
            title: `${approver.total || 0} việc đang chờ bạn duyệt`,
          },
          '/leave-requests': {
            count: (leaveQueue?.count || 0) + myLeave.length,
            level: worst(leaveQueue?.level || 'ok', ...myLeave.map((i) => i.level)),
            title: [
              leaveQueue?.count ? `${leaveQueue.count} đơn nghỉ chờ bạn duyệt` : null,
              myLeave.length ? `${myLeave.length} đơn của bạn đang chờ duyệt` : null,
            ].filter(Boolean).join(' · '),
          },
        });
      } catch {
        // Hộp duyệt lỗi không được làm hỏng sidebar — chỉ là không có số để hiện.
        if (!cancelled) setNavBadges({});
      }
    };

    load();
    // Cùng nhịp với bộ đếm thông báo chưa đọc (60 giây).
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.employee_id, location.pathname]);

  // Cờ "được phép ủy quyền quản lý phòng"
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api
      .get('/department-delegation/can')
      .then((res) => {
        if (!cancelled) setCanDelegate(!!res.data?.data?.can_delegate);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.employee_id]);

  const navigation = buildNavigation();
  if (canDelegate && !navigation.some((n) => n.href === '/department-delegation')) {
    navigation.push({ name: 'Ủy quyền quản lý phòng', href: '/department-delegation', icon: UserCog });
  }

  // Active theo prefix → mục cha sáng ở route con
  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // Tiêu đề header: khớp nav theo prefix (dài nhất), fallback EXTRA_TITLES → site name
  const pageTitle = useMemo(() => {
    let best = null;
    for (const item of navigation) {
      if (isActive(item.href) && (!best || item.href.length > best.href.length)) best = item;
    }
    if (best) return best.name;
    if (EXTRA_TITLES[location.pathname]) return EXTRA_TITLES[location.pathname];
    return siteConfig?.site_name || 'Dashboard';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation, location.pathname, siteConfig?.site_name]);

  // Gom nav thành nhóm (giữ thứ tự nhóm + thứ tự item trong nhóm)
  const groupedNav = useMemo(() => {
    const buckets = new Map();
    for (const item of navigation) {
      const gid = groupIdOf(item.href);
      if (!buckets.has(gid)) buckets.set(gid, []);
      buckets.get(gid).push(item);
    }
    const ordered = NAV_GROUPS.filter((g) => buckets.has(g.id)).map((g) => ({
      ...g,
      items: buckets.get(g.id),
    }));
    if (buckets.has('other')) ordered.push({ id: 'other', label: 'Khác', items: buckets.get('other') });
    return ordered;
  }, [navigation]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.classList.toggle('dark', next);
    try {
      localStorage.setItem('theme', next ? 'dark' : 'light');
    } catch {}
  };

  const toggleGroup = (id) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next]));
      } catch {}
      return next;
    });
  };

  const renderNavItem = (item) => {
    const active = isActive(item.href);
    const badge = navBadges[item.href];
    return (
      <Link
        key={item.name + item.href}
        to={item.href}
        aria-current={active ? 'page' : undefined}
        className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
          active
            ? 'bg-gray-100 text-brand-fg-strong dark:bg-gray-800'
            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
        } ${item.isChild ? 'pl-9' : ''}`}
        onClick={() => setSidebarOpen(false)}
      >
        <item.icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-fg' : ''}`} aria-hidden="true" />
        <span className="truncate flex-1">{item.name}</span>
        {badge && badge.count > 0 && (
          <span
            title={badge.title}
            // Màu KHÔNG phải là thông tin duy nhất: con số luôn có, và title/aria nói
            // rõ nghĩa — người không phân biệt được màu vẫn đọc được mức độ.
            aria-label={badge.title}
            className={`ml-auto min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-[11px] font-bold leading-none text-center ${
              badge.level === 'overdue'
                ? 'bg-red-600 text-white'
                : badge.level === 'warn'
                  ? 'bg-amber-500 text-white'
                  : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
            }`}
          >
            {badge.count > 99 ? '99+' : badge.count}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transform transition-transform duration-300 ease-in-out lg:translate-x-0 safe-top safe-bottom safe-left ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={(!darkMode && siteConfig?.sidebar_bg_color) ? { backgroundColor: siteConfig.sidebar_bg_color } : {}}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div
            className="flex items-center justify-between h-16 px-5 border-b border-gray-200 dark:border-gray-800"
            style={(!darkMode && siteConfig?.sidebar_bg_color) ? { backgroundColor: siteConfig.sidebar_bg_color, borderColor: 'rgba(0,0,0,0.1)' } : {}}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <img
                src="/logo192.png?v=20260620"
                alt={siteConfig?.site_name || 'HR System'}
                className="h-9 w-9 rounded-xl object-cover flex-shrink-0"
              />
              <span
                className="text-base font-bold text-brand-fg-strong truncate"
                style={(!darkMode && siteConfig?.primary_color) ? { color: siteConfig.primary_color } : {}}
              >
                {siteConfig?.site_name || 'HR System'}
              </span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              aria-label="Đóng menu"
              className="lg:hidden rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation (nhóm gập) */}
          <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto" aria-label="Điều hướng chính">
            {groupedNav.map((group) => {
              const collapsed = collapsedGroups.has(group.id);
              return (
                <div key={group.id}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    aria-expanded={!collapsed}
                    className="flex items-center justify-between w-full px-3 mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <span>{group.label}</span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${collapsed ? '-rotate-90' : ''}`}
                      aria-hidden="true"
                    />
                  </button>
                  {!collapsed && <div className="space-y-0.5">{group.items.map(renderNavItem)}</div>}
                </div>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="border-t border-gray-200 dark:border-gray-800 p-3">
            <div className="flex items-center gap-3 mb-3 px-1">
              <div className="w-10 h-10 rounded-full bg-brand-softer flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-brand-fg-strong" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.full_name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.role?.role_name}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 dark:bg-red-950 dark:text-red-300 dark:hover:bg-red-900 transition-colors"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              Đăng xuất
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64 safe-bottom">
        {/* Top Bar */}
        <header
          className="sticky top-0 z-10 bg-white/90 dark:bg-gray-900/90 backdrop-blur border-b border-gray-200 dark:border-gray-800 safe-top safe-left safe-right"
          style={(!darkMode && siteConfig?.header_bg_color) ? { backgroundColor: siteConfig.header_bg_color } : {}}
        >
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                aria-label="Mở menu"
                className="lg:hidden rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1
                className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate"
                style={(!darkMode && siteConfig?.header_text_color) ? { color: siteConfig.header_text_color } : {}}
              >
                {pageTitle}
              </h1>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 sm:gap-2">
              <Link
                to="/attendance"
                aria-label="Chấm công nhanh"
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Chấm công"
              >
                <ClipboardCheck className="w-5 h-5" />
              </Link>

              {/* Notification Bell */}
              <div className="relative">
                <button
                  data-notification-bell
                  onClick={togglePanel}
                  aria-label={`Thông báo${unreadCount > 0 ? ` (${unreadCount} chưa đọc)` : ''}`}
                  className={`relative p-2 rounded-lg transition-colors ${
                    isPanelOpen
                      ? 'text-brand-fg bg-brand-softer'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  title="Thông báo"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 animate-pulse">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                <NotificationPanel />
              </div>

              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                aria-label={darkMode ? 'Chuyển sang giao diện sáng' : 'Chuyển sang giao diện tối'}
                aria-pressed={darkMode}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* Profile Link */}
              <Link
                to="/profile"
                className="flex items-center gap-2 ml-1 text-sm font-medium text-gray-700 hover:text-brand-fg dark:text-gray-300 dark:hover:text-brand-fg rounded-lg px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <User className="w-5 h-5" aria-hidden="true" />
                <span className="hidden sm:inline">Hồ sơ</span>
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
        {/* Hỏi "ăn cơm thế nào?" khi chấm công ở nơi khác nơi làm việc (chỉ hoạt động khi backend bật tính năng) */}
        <MealTravelPromptHost />
      </div>
    </div>
  );
}
